import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import { createKbSplitDraftArticles, proposeKbDocumentSplit } from "./kbAdminSplitWorkflow";
import { runKbBulkImport } from "./runKbBulkImport";
import type { KbAudience, KbScope, KbStatus, PanelNote } from "./types";

type ShowNote = (note: PanelNote | null) => void;

type Params = {
  articleContent: string;
  articleTitle: string;
  articleDocType: string;
  splitProposals: any[];
  hasEditorName: boolean;
  editorName: string;
  articleAudience: KbAudience;
  articleScope: KbScope;
  articleStatus: KbStatus;
  articleSlug: string;
  bulkImportText: string;
  bulkAllowPolicyOverwrite: boolean;
  canPublishKb: boolean;
  setAiHelpOpen: Dispatch<SetStateAction<boolean>>;
  setSplitWorking: Dispatch<SetStateAction<boolean>>;
  setSplitOpen: Dispatch<SetStateAction<boolean>>;
  setSplitProposals: Dispatch<SetStateAction<any[]>>;
  setSplitCreating: Dispatch<SetStateAction<boolean>>;
  setIsBulkImporting: Dispatch<SetStateAction<boolean>>;
  setBulkImportText: (v: string) => void;
  setKbMode: (v: any) => void;
  refreshDraftCount: () => Promise<void>;
  showNote: ShowNote;
};

export function useKbImportAndSplitWorkflow(p: Params) {
  async function doProposeSplit() {
    p.setAiHelpOpen(false);
    await proposeKbDocumentSplit({
      articleContent: p.articleContent,
      articleTitle: p.articleTitle,
      articleDocType: p.articleDocType,
      setSplitWorking: p.setSplitWorking,
      setSplitOpen: p.setSplitOpen,
      setSplitProposals: p.setSplitProposals,
      showNote: p.showNote,
    });
  }

  async function doCreateSplitStubs() {
    await createKbSplitDraftArticles({
      splitProposals: p.splitProposals,
      hasEditorName: p.hasEditorName,
      editorName: p.editorName,
      articleAudience: p.articleAudience,
      articleScope: p.articleScope,
      articleDocType: p.articleDocType,
      articleTitle: p.articleTitle,
      articleSlug: p.articleSlug,
      setSplitCreating: p.setSplitCreating,
      setSplitProposals: p.setSplitProposals,
      showNote: p.showNote,
      refreshDraftCount: p.refreshDraftCount,
    });
  }

  async function doBulkImport() {
    await runKbBulkImport({
      bulkImportText: p.bulkImportText,
      hasEditorName: p.hasEditorName,
      articleAudience: p.articleAudience,
      articleScope: p.articleScope,
      articleDocType: p.articleDocType,
      articleStatus: p.articleStatus,
      canPublishKb: p.canPublishKb,
      editorName: p.editorName,
      allowPolicyOverwrite: p.bulkAllowPolicyOverwrite,
      showNote: p.showNote,
      setIsBulkImporting: p.setIsBulkImporting,
      refreshDraftCount: p.refreshDraftCount,
    });
  }

  async function onBulkImportFilePicked(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      try { JSON.parse(text); } catch {
        p.showNote({ tone: "error", title: "Not a valid JSON file", text: `"${file.name}" could not be read as JSON. The bulk importer only accepts .json files containing an array of article objects. PDFs and other document formats are not supported here.` });
        return;
      }
      p.setBulkImportText(text);
      p.setKbMode("import");
      p.showNote({ tone: "info", title: "File loaded into Bulk Import", text: `"${file.name}" was pasted into the import text area below. Review the content, then click Run import to create the articles.` });
    } catch (error) {
      p.showNote({ tone: "error", title: "File load failed", text: error instanceof Error ? error.message : String(error) });
    } finally {
      event.target.value = "";
    }
  }

  return { doProposeSplit, doCreateSplitStubs, doBulkImport, onBulkImportFilePicked };
}
